import React from 'react';
import PropTypes from 'prop-types';
import { LuLayoutGrid } from 'react-icons/lu';

import Modal from '../../../components/Modal/Modal';
import { ModalScroll, GroupBlock, ItemGrid, ItemLink } from '../styles';

const ITEM_ICON_SIZE = 18;

/**
 * Nível 2 da navegação faseada: itens do módulo, agrupados por subgrupo.
 *
 * Usa o `Modal` compartilhado (FC7 cap.2 §H.2 — nunca forjar modal/backdrop/
 * focus-trap à mão). Ele já entrega portal, `role="dialog"`, `aria-modal`,
 * focus-trap com ciclo de Tab, devolução de foco ao fechar, Esc, clique no
 * backdrop e trava do scroll do body — todo o §6 do handoff, exceto o slot de
 * ícone, que virou a prop `titleIcon` do primitivo.
 *
 * O scroll de módulos grandes (§2.2) fica no ModalScroll, aqui dentro — sem
 * tocar no `Body` do primitivo compartilhado.
 */
function ModuleModal({ module: mod = null, isOpen, onClose, onNavigate }) {
   const Icon = mod?.icon || LuLayoutGrid;

   return (
      <Modal
         isOpen={isOpen}
         onClose={onClose}
         title={mod?.nome || ''}
         titleIcon={<Icon size={20} strokeWidth={1.7} />}
         maxWidth="760px"
      >
         <ModalScroll>
            {(mod?.grupos || []).map((grupo, index) => (
               // Bloco sem rótulo (topItems) não tem `nome` — o índice compõe
               // a chave para não colidir com outro bloco anônimo.
               <GroupBlock key={grupo.nome || `sem-grupo-${index}`}>
                  {grupo.nome && <h4>{grupo.nome}</h4>}

                  <ItemGrid>
                     {grupo.itens.map((item) => {
                        const ItemIcon = item.icon;
                        return (
                           <ItemLink
                              key={item.to}
                              to={item.to}
                              onClick={onNavigate}
                           >
                              <span className="item-icon" aria-hidden="true">
                                 {ItemIcon ? (
                                    <ItemIcon size={ITEM_ICON_SIZE} />
                                 ) : null}
                              </span>
                              <span className="item-label">{item.label}</span>
                              {item.novo && (
                                 <>
                                    <span
                                       className="item-new-dot"
                                       title="Novo"
                                       aria-hidden="true"
                                    />
                                    <span className="sr-only">Novo</span>
                                 </>
                              )}
                           </ItemLink>
                        );
                     })}
                  </ItemGrid>
               </GroupBlock>
            ))}
         </ModalScroll>
      </Modal>
   );
}

ModuleModal.propTypes = {
   // eslint-disable-next-line react/require-default-props
   module: PropTypes.shape({
      nome: PropTypes.string,
      icon: PropTypes.elementType,
      grupos: PropTypes.arrayOf(
         PropTypes.shape({
            nome: PropTypes.string,
            itens: PropTypes.arrayOf(
               PropTypes.shape({
                  to: PropTypes.string.isRequired,
                  label: PropTypes.string.isRequired,
                  icon: PropTypes.elementType,
                  novo: PropTypes.bool,
               })
            ).isRequired,
         })
      ),
   }),
   isOpen: PropTypes.bool.isRequired,
   onClose: PropTypes.func.isRequired,
   onNavigate: PropTypes.func.isRequired,
};

export default ModuleModal;
